import React from 'react';
import { motion, HTMLMotionProps } from 'framer-motion';

interface ButtonProps extends HTMLMotionProps<'button'> {
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  children: React.ReactNode;
  momentumStatus?: 'Building' | 'Maintaining' | 'Declining';
  ambient?: boolean; // Use new ambient styling
}

const Button: React.FC<ButtonProps> = ({ 
  variant = 'primary', 
  size = 'md', 
  className = '', 
  children, 
  momentumStatus,
  ambient = false,
  ...props 
}) => {
  // Use ambient styling if specified, otherwise fall back to momentum styling
  if (ambient) {
    const ambientBaseClasses = 'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';
    
    const ambientVariantClasses = {
      primary: 'btn-primary text-button-primary-text bg-button-primary-bg dark:bg-white dark:text-black hover:shadow-md hover:-translate-y-0.5 hover:bg-gradient-to-r hover:from-rose-600 hover:to-blue-600 hover:text-white transition-all duration-300',
      secondary: 'btn-secondary text-button-secondary-text bg-button-secondary-bg hover:bg-button-secondary-hover',
      ghost: 'text-text-secondary hover:text-text-primary hover:bg-gray-100',
    };
    
    const sizeClasses = {
      sm: 'px-3 py-2 text-sm min-h-[40px]',
      md: 'px-4 py-2.5 text-base min-h-[44px]', 
      lg: 'px-6 py-3 text-lg min-h-[48px]'
    };
    
    const ambientClasses = `${ambientBaseClasses} ${ambientVariantClasses[variant]} ${sizeClasses[size]} ${className}`;
    
    return (
      <motion.button
        whileHover={{ scale: variant === 'primary' ? 1.02 : 1.01 }}
        whileTap={{ scale: 0.98 }}
        className={ambientClasses}
        {...props}
      >
        {children}
      </motion.button>
    );
  }
  
  // Legacy momentum styling
  let gradientClass = '';
  if (momentumStatus === 'Building') gradientClass = 'bg-momentum-building';
  else if (momentumStatus === 'Maintaining') gradientClass = 'bg-momentum-maintaining';
  else if (momentumStatus === 'Declining') gradientClass = 'bg-momentum-declining';

  const baseClasses = 'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed glass';
  
  const variantClasses = {
    primary: `${gradientClass || 'bg-momentum-building'} text-white hover:opacity-90 focus:ring-momentum-teal`,
    secondary: 'bg-dark-glass text-momentum-emerald hover:bg-momentum-emerald/20 focus:ring-momentum-emerald',
    ghost: 'text-momentum-amber hover:text-white hover:bg-momentum-amber/20 focus:ring-momentum-amber',
  };
  
  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg'
  };
  
  const classes = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;

  return (
    <motion.button
      whileHover={{ scale: 1.04 }}
      whileTap={{ scale: 0.97 }}
      className={classes}
      {...props}
    >
      {children}
    </motion.button>
  );
};

export default Button; 